import { fetchAccount } from "../../api";
import { CoalescedPod, CoalescedPodProvider, Pod, PodHistoryEntry, PodProvider, PodProviderCategory } from "../../api/pods.types";
import { ProviderData } from "../../api/providers.types";

/**
 * Infer the position of a provider based on its suffix (i.e., professional title).
 * @param provider An object containing a `suffix` property. 
 * Typically a PodProvider, but may also be a ProviderData or an immediate.
 * @returns A string representation of the provider's inferred position in a pod, or undefined.
 */
 export const getProviderPosition = (provider: Pick<ProviderData, 'suffix'>) => {
  switch (provider.suffix.toUpperCase()) {
    case 'MD':
    case 'DO':
      return 'Physician';
    case 'NP':
      return 'Nurse Practitioner';
    case 'DNP':
      return 'Doctor of Nursing Practice';
    case 'PA':
      return 'Physician Assistant';
    case 'LVN': // Licensed Vocational Nurse
    case 'LPN': // Licensed Practical Nurse
    case 'LVN/LPN':
      return 'Licensed Nurse';
    case 'RN':
      return 'Registered Nurse';
    case 'MA':
      return 'Medical Assistant';
    case 'PCC':
      return 'Patient Care Coordinator';
    case 'PSC':
      return 'Patient Support Coordinator';
    default:
      return undefined;
  }
}

/**
 * Infer the category into which a provider should be binned based on its position.
 * @param provider An object containing a `position` property.
 * Typically a PodProvider, but may also be an immediate.
 * @returns A string representation of the category into which the provider should be binned, or undefined.
 */
export const getProviderCategoryByPosition = (provider: Pick<PodProvider, 'position'>) => {
  switch (provider.position) {
    case 'Physician':
      return 'Physician';
    case 'Nurse Practitioner':
    case 'Doctor of Nursing Practice':
    case 'Physician Assistant':
    case 'Licensed Vocational Nurse':
    case 'Licensed Practical Nurse':
    case 'Licensed Nurse':
    case 'Registered Nurse':
      return 'Clinician';
    case 'Medical Assistant':
    case 'Patient Care Coordinator':
    case 'Patient Support Coordinator':
      return 'Support Staff';
    default:
      return undefined;
  }
}

/**
 * Infer the category into which a provider should be binned based on
 * its suffix (i.e. professional title).
 * @param provider An object containing a `suffix` property.
 * Typically a PodProvider, but may also be a ProviderData or an immediate.
 * @returns A string representation of the category into which the provider should be binned, or undefined.
 */
export const getProviderCategory = (provider: Pick<ProviderData, 'suffix'>): PodProviderCategory | undefined => {
  switch (provider.suffix.toUpperCase()) {
    case 'MD':
    case 'DO':
      return 'Physician';
    case 'NP':
    case 'DNP':
    case 'PA':
    case 'LVN':
    case 'LPN':
    case 'LVN/LPN':
    case 'RN':
      return 'Clinician';
    case 'MA':
    case 'PCC':
    case 'PSC':
      return 'Support Staff';
    default:
      return undefined;
  }
}

/**
 * Retrieve the full name associated with a user account based on its ID.
 * @note This method references a const at the top level of Util.ts for caching, so
 * it generally shouldn't be exported without first decoupling this dependency.
 * @param id A string ID belonging to a CONSUMER type account.
 * @returns A string containing the first and last name of the account owner.
 */
const getAccountNameById = async (id: string | null): Promise<string> => {
  if (!id) return 'A user';

  return fetchAccount(id)
    .then(data => {
      if (data.firstName && data.lastName) return `${data.firstName} ${data.lastName}`;
      else throw SyntaxError('The name is missing one or more parts');
    })
    .catch(err => {
      console.error(`Unable to fetch account ${id}:`, err);
      return 'A user';
    });
}

export const coalesceAndSortProviders = async (pod: Pod): Promise<CoalescedPod> => {
  const providers = new Map<PodProvider['providerId'], CoalescedPodProvider>();
  const allActors = new Set<PodProvider['deactivatedBy']>();
  const actorNames = new Map<PodProvider['deactivatedBy'], string>();

  for (const provider of pod.providers) {
    allActors.add(provider.activatedBy);
    if (provider.status === 'DEACTIVATED') {
      allActors.add(provider.deactivatedBy);
    }
  }

  const actorRequests = Array.from(allActors).map(id => getAccountNameById(id).then(name => [id, name] as const));
  await Promise.all(actorRequests).then(results => {
    for (const result of results) {
      actorNames.set(result[0], result[1]);
    }
  });

  for (let i = 0; i < pod.providers.length; i++) {
    const provider = pod.providers[i];
    const existingHistory = providers.has(provider.providerId) ? providers.get(provider.providerId)!.history : [];
    
    const subjectName = provider?.firstName && provider?.lastName
      ? `${provider.firstName} ${provider.lastName}${provider.suffix ? `, ${provider.suffix}` : ''}`
      : 'this provider';

    const newHistory: PodHistoryEntry[] = [
      ...existingHistory, 
      {
        id: provider.id,
        status: 'ACTIVATED',
        date: provider.activatedAt,
        actor: {
          id: provider.activatedBy,
          name: actorNames.get(provider.activatedBy) ?? 'A user',
        },
        subject: subjectName,
      }
    ];

    if (provider.status === 'DEACTIVATED') {
      newHistory.push({
        id: provider.id,
        status: 'DEACTIVATED',
        date: provider.deactivatedAt!,
        actor: {
          id: provider.deactivatedBy!,
          name: actorNames.get(provider.deactivatedBy) ?? 'A user',
        },
        subject: subjectName,
      })
    }

    const coalescedProvider: CoalescedPodProvider = {
      ...provider,
      history: newHistory
    };

    providers.set(provider.providerId, coalescedProvider);
  }

  const providerList = Array.from(providers.values());

  providerList.sort((a,b) => {
    const positionCompare = a.position?.localeCompare(b.position);
    if (positionCompare !== undefined && positionCompare !== 0) {
      return positionCompare;
    } else {
      const aName = `${a.firstName} ${a.lastName}`;
      const bName = `${b.firstName} ${b.lastName}`;

      return aName.localeCompare(bName);
    }
  });

  return {
    ...pod,
    providers: providerList,
  };
}